/**
 * The settings screen: theme appearance, account management, and app info.
 *
 * Always available as a primary destination (/settings) across all modes (private, guest, public).
 */

import { useState } from "react";
import type { FormEvent, ReactElement } from "react";

import { copy } from "../copy.ts";
import { messageFor } from "../lib/errors.ts";
import { getStoredGeminiKey, setStoredGeminiKey } from "../lib/geminiKey.ts";
import { useRevokeSession, useSessions } from "../lib/queries.ts";
import { useCurrentUser, usePublicMode, useSessionManager } from "../lib/session-context.tsx";
import { useTheme, type ThemeMode } from "../lib/theme.ts";
import { useToast } from "../components/Toast.tsx";

/** Vietnam time, from the API's offset-bearing timestamp, without a timezone library. */
function whenLabel(iso: string | null): string {
  if (iso === null) return copy.state.unknown;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return copy.state.unknown;
  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "short", timeStyle: "short" }).format(date);
}

export function SettingsScreen(): ReactElement {
  const manager = useSessionManager();
  const user = useCurrentUser();
  const hasAccount = usePublicMode() === "private";
  const userId = user?.id ?? "";
  const sessions = useSessions(userId, hasAccount);
  const revokeSession = useRevokeSession(userId);

  const [claiming, setClaiming] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const { theme, setTheme } = useTheme();
  const { showToast } = useToast();
  const [geminiKey, setGeminiKey] = useState(() => getStoredGeminiKey());

  function onSelectTheme(mode: ThemeMode): void {
    setTheme(mode);
  }

  async function onClaim(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await manager.claimWithPassword(email, password);
      setClaiming(false);
      setEmail("");
      setPassword("");
    } catch (caught) {
      setError(caught);
    }
  }

  const isLight = theme === "light";
  const isDark = theme === "dark";
  const isSystem = theme === "system";

  return (
    <div className="screen">
      <div className="screen__head">
        <h1 className="screen__heading">{copy.settings.heading}</h1>
        <p className="screen__sub">{copy.settings.intro}</p>
      </div>

      {/* Theme / Appearance Section */}
      <section className="section">
        <h2 className="section__heading">{copy.settings.themeTitle}</h2>
        <p className="section__note">{copy.settings.themeSubtitle}</p>
        <div className="segmented" role="tablist">
          <button
            type="button"
            className={isLight ? "segmented__option segmented__option--on" : "segmented__option"}
            onClick={() => onSelectTheme("light")}
          >
            {copy.settings.themeLight}
          </button>
          <button
            type="button"
            className={isDark ? "segmented__option segmented__option--on" : "segmented__option"}
            onClick={() => onSelectTheme("dark")}
          >
            {copy.settings.themeDark}
          </button>
          <button
            type="button"
            className={isSystem ? "segmented__option segmented__option--on" : "segmented__option"}
            onClick={() => onSelectTheme("system")}
          >
            {copy.settings.themeSystem}
          </button>
        </div>
      </section>

      {/* AI & Gemini BYOK Section */}
      <section className="section">
        <h2 className="section__heading">{copy.settings.geminiTitle}</h2>
        <p className="section__note">{copy.settings.geminiSubtitle}</p>
        <div className="form">
          <label className="field">
            <span className="field__label">{copy.settings.geminiKeyLabel}</span>
            <input
              className="field__input"
              type="password"
              placeholder={copy.settings.geminiKeyPlaceholder}
              value={geminiKey}
              onChange={(e) => setGeminiKey(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <span className="field__help">{copy.settings.geminiKeyHelp}</span>
          </label>
          <div>
            <button
              type="button"
              className="button button--primary"
              onClick={() => {
                setStoredGeminiKey(geminiKey);
                showToast(geminiKey.trim() ? copy.settings.geminiKeySaved : copy.settings.geminiKeyCleared);
              }}
            >
              {copy.settings.geminiKeySave}
            </button>
            {geminiKey.trim() !== "" && (
              <button
                type="button"
                className="button button--ghost"
                onClick={() => {
                  setGeminiKey("");
                  setStoredGeminiKey("");
                  showToast(copy.settings.geminiKeyCleared);
                }}
              >
                {copy.settings.geminiKeyClear}
              </button>
            )}
          </div>
          <p className="section__note">
            <a
              href="https://aistudio.google.com/app/apikey"
              target="_blank"
              rel="noreferrer noopener"
              className="link"
            >
              {copy.settings.geminiGetFreeKey}
            </a>
          </p>
        </div>
      </section>

      {/* Account Section */}
      <section className="section">
        <h2 className="section__heading">{copy.settings.accountTitle}</h2>

        {user !== null && (
          <dl className="facts">
            <dt>{copy.signIn.displayName}</dt>
            <dd>{user.display_name}</dd>
            <dt>{copy.signIn.email}</dt>
            <dd>{user.email ?? copy.state.unknown}</dd>
          </dl>
        )}

        {!hasAccount && <p className="section__note">{copy.profile.sharedNote}</p>}

        {hasAccount && user?.is_guest && (
          <div>
            <p className="section__note">{copy.signIn.guestNote}</p>
            <button type="button" className="button button--primary" onClick={() => setClaiming((v) => !v)}>
              {copy.signIn.claim}
            </button>

            {claiming && (
              <form className="form" onSubmit={(event) => void onClaim(event)}>
                <label className="field">
                  <span className="field__label">{copy.signIn.email}</span>
                  <input
                    className="field__input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    inputMode="email"
                    autoCapitalize="none"
                    required
                  />
                </label>
                <label className="field">
                  <span className="field__label">{copy.signIn.password}</span>
                  <input
                    className="field__input"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </label>
                {error !== null && (
                  <p className="form__error" role="alert">
                    {messageFor(error)}
                  </p>
                )}
                <button type="submit" className="button button--primary">
                  {copy.signIn.claim}
                </button>
              </form>
            )}
          </div>
        )}

        {hasAccount && (
          <div>
            <h3 className="section__heading">{copy.profile.sessions}</h3>

            {sessions.isPending && <p className="state">{copy.state.loading}</p>}
            {sessions.isError && (
              <p className="state state--error" role="alert">
                {messageFor(sessions.error)}
              </p>
            )}

            {sessions.isSuccess && (
              <ul className="cards">
                {sessions.data.sessions.map((session) => (
                  <li key={session.id} className="card card--row">
                    <div>
                      <span className="card__title">
                        {session.client}
                        {session.current && <span className="badge">{copy.profile.thisDevice}</span>}
                      </span>
                      <span className="card__meta">
                        {copy.profile.sessionUpdated(whenLabel(session.last_used_at ?? session.created_at))}
                      </span>
                    </div>
                    {!session.current && (
                      <button
                        type="button"
                        className="link link--danger"
                        onClick={() => revokeSession.mutate(session.id)}
                        disabled={revokeSession.isPending}
                      >
                        {copy.profile.signOutDevice}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <button type="button" className="button" onClick={() => void manager.signOut()}>
              {copy.profile.signOut}
            </button>
          </div>
        )}
      </section>

      {/* About Application Section */}
      <section className="section">
        <h2 className="section__heading">{copy.settings.aboutTitle}</h2>
        <p className="section__note">{copy.settings.aboutDesc}</p>
        <dl className="facts">
          <dt>{copy.settings.versionLabel}</dt>
          <dd>{copy.settings.versionValue}</dd>
        </dl>
      </section>
    </div>
  );
}
